/**
 * `tuninho:chart` — o bloco de gráfico.
 *
 * ## A degradação segura (T3.4), e por que ela já está garantida
 *
 * O card marca como risco *"um documento com `tuninho:chart` aberto por cliente que não o
 * conhece não pode quebrar"*. Medido na árvore vendorizada, ele **não quebra**, e por dois
 * mecanismos independentes do upstream:
 *
 * ```ts
 * // framework/store/src/model/store/store.ts:649
 * private _onBlockAdded(id, isLocal, init) { try { … } catch (e) { console.error(…) } }
 *
 * // framework/store/src/model/block/block-model.ts:24-29
 * this._children.value.forEach(id => { const block = this._store.getBlock$(id); if (block) {…} })
 * ```
 *
 * Flavour desconhecido: `Block` lança em `sync-controller.ts:295`, o `try/catch` engole, o
 * filho não entra em `children` e **o documento carrega inteiro**. E o bloco continua no
 * Y.Doc — o dado não se perde, só não se desenha.
 *
 * ⚠️ Isso torna a T3.4 uma questão de **teste**, não de desenho. E o teste importa: os dois
 * mecanismos são internos do upstream, e nada impede uma versão futura de trocar o `try/catch`
 * por um `throw`. `cli/tests/test_bloco_de_grafico.py` afirma sobre os dois trechos, e fica
 * vermelho no dia em que qualquer um sumir — que é o dia em que precisamos saber.
 *
 * ## Por que os `props` são planos
 *
 * `sourceBlockId`, `tipo`, `eixoX`, `agregacao`, `titulo` e `altura` são escalares; `series` é
 * um array de objetos. O `native2Y` do `crud.ts` converte os dois, mas escalar é o que
 * atravessa melhor o CRDT — dois usuários mexendo em campos diferentes não conflitam.
 *
 * ⚠️ `series` é o único campo composto, e ele é substituído inteiro em vez de mutado item a
 * item. Mutar um `Y.Array` de `Y.Map` a partir de dois clientes produz mesclagem que ninguém
 * pediu (a série do outro reaparecendo); substituir o array inteiro faz o último a salvar
 * ganhar, que é o comportamento previsível para uma configuração.
 */

import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

import type { Agregacao, Serie, TipoDeGrafico } from './dados';

export const FLAVOUR_DO_GRAFICO = 'tuninho:chart';

export type TuninhoChartProps = {
  /** O id do bloco `affine:database` do MESMO documento que alimenta o gráfico. */
  sourceBlockId: string;
  /** O id da visão a respeitar (filtro, ordenação). Vazio = a visão corrente. */
  viewId: string;
  tipo: TipoDeGrafico;
  /** `propertyId` da coluna que vira o eixo X (ou a fatia, em pizza/rosca). */
  eixoX: string;
  series: Serie[];
  agregacao: Agregacao;
  titulo: string;
  altura: number;
};

export class TuninhoChartBlockModel extends BlockModel<TuninhoChartProps> {}

export const TuninhoChartBlockSchema = defineBlockSchema({
  flavour: FLAVOUR_DO_GRAFICO,
  props: (): TuninhoChartProps => ({
    sourceBlockId: '',
    viewId: '',
    tipo: 'barra',
    eixoX: '',
    series: [],
    agregacao: 'soma',
    titulo: '',
    altura: 320,
  }),
  metadata: {
    version: 1,
    // `content` e não `hub`: o gráfico não tem filhos. Declarar `hub` permitiria arrastar
    // parágrafos para dentro dele, o que não faz sentido nenhum e viraria dado órfão.
    role: 'content',
    parent: ['affine:note'],
    children: [],
  },
  toModel: () => new TuninhoChartBlockModel(),
});

export const TuninhoChartBlockSchemaExtension =
  BlockSchemaExtension(TuninhoChartBlockSchema);
